/**
 * NON_NAV_SHELL_PATHS — every path that renders INSIDE <AppShell> but carries
 * no `APP_NAV_ITEMS` entry, because you reach it from a list row or an in-app
 * action rather than from the sidebar.
 *
 * ONE source of truth, two consumers:
 *
 *   1. `routes/index.jsx` — registers a <Route> for each path.
 *   2. `components/AppShell.jsx` — decides whether the current URL is a real
 *      in-shell destination before it renders <Outlet />.
 *
 * WHY this file exists. Those two lists used to be maintained by hand in two
 * places, and they drifted: the Phase 6 Customer 360° record pages
 * (`/contacts/:contactId` and friends) were added to the router but never to
 * AppShell's allow-list, so every record page rendered
 * "Page not available for your account" — for EVERY user, including a super
 * admin holding every scope, because that gate matches on URL SHAPE and never
 * looks at scopes at all. Adding a param-bearing module now means adding its
 * pattern HERE, once, and both consumers follow.
 *
 * Patterns are react-router v6 path patterns — `matchPath()` resolves the
 * `:params`, and matching is exact (`end: true`), so `/contacts/:contactId`
 * accepts `/contacts/1` but not `/contacts/1/anything`.
 *
 * SCOPE NOTE (Rule 65). Presence here grants no data access. These paths are
 * deliberately NOT scope-gated in the shell: the gate lives at the entry point
 * that links to them (a row is only rendered from a list the user could load)
 * and, authoritatively, on the backend — every underlying route sits behind
 * `oauth.scope` middleware. The shell's job is only to recognise the path as
 * a legitimate destination.
 */
import { FORM_FULL_PAGE_ROUTES } from '../forms/registry';

const FORM_PAGE_PATHS = FORM_FULL_PAGE_ROUTES.map((route) => route.path);

export const NON_NAV_SHELL_PATHS = [
  // ── Account / profile ──────────────────────────────────────────────────
  '/profile',
  '/profile/sessions',
  '/profile/change-password',
  '/profile/mfa',
  '/profile/account-security',
  '/profile/linked-accounts',
  '/profile/complete',

  // ── Phase 6 — Customer 360° record pages (reached from list rows) ───────
  '/contacts/:contactId',
  '/organizations/:organizationId',
  '/leads/:leadId',
  '/opportunities/:opportunityId',

  // ── Rule 74 — Voice + Video Calls (reached by starting/accepting a call) ─
  '/call/:callId',
  '/threads/:threadId/calls',

  // ── Add/Edit forms opened as their own page (`/leads/new`, `/tags/7/edit`).
  //    Derived from `forms/registry.js` rather than re-typed, so a form can
  //    never be routable here and unknown to the shell (or the reverse).
  //    Static-vs-dynamic ranking in react-router v6 is what keeps `/leads/new`
  //    from being swallowed by `/leads/:leadId` above.
  ...FORM_PAGE_PATHS,
];

export default NON_NAV_SHELL_PATHS;
